import { useUtilisateurService } from '@europrocurement/l2d-domain';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { castIriToId } from '@europrocurement/l2d-utils';
import { DataProviderProps, DataState } from './types';

type User = {
    id: number;
    firstName: string;
    lastName: string;
    keycloakUserIdentifier?: string;
    mail: string;
    deleted: boolean;
    formalist: boolean;
    service: {
        id: number;
        label: string;
    };
    workGroups: Array<{
        id: number;
        idUserReferent?: number;
        label: string;
    }>;
};

type UserState = DataState & {
    data: Array<User>;
};

const INIT_ITEMS_CTX = 'init_users_ctx';

type Action = { type: 'init_users_ctx'; payload: Array<User> };

export const UsersContext = createContext<UserState | null>(null);
export const UsersDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function usersReducer(state: UserState, action: Action) {
    switch (action.type) {
        case INIT_ITEMS_CTX: {
            return {
                loaded: true,
                data: [...action.payload],
            };
        }
        default: {
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

export const UsersProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(usersReducer, { loaded: false, data: [] });
        const { utilisateurApi } = useUtilisateurService();

        useEffect(() => {
            if (!autoload) {
                return;
            }

            utilisateurApi
                .apiUtilisateursGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_ITEMS_CTX,
                        payload: response.data['hydra:member'].map((user) => ({
                            id: user.id as number,
                            firstName: user.prenom as string,
                            lastName: user.nom as string,
                            keycloakUserIdentifier: user?.userIdentifier
                                ? (user?.userIdentifier as string)
                                : undefined,
                            mail: user.mail as string,
                            deleted: user.isDeleted as boolean,
                            formalist: user.isFormalist as boolean,
                            service: {
                                id: user?.service?.id as number,
                                label: user?.service?.libelle as string,
                            },
                            workGroups: (user?.groupeTravail || []).map((group) => {
                                let idUserReferent = group?.iduserReferent?.id;

                                if (!idUserReferent && group?.iduserReferent) {
                                    idUserReferent = castIriToId(group.iduserReferent as string);
                                }

                                return {
                                    id: group.id as number,
                                    idUserReferent,
                                    label: group.lib as string,
                                };
                            }),
                        })),
                    });
                })
                .catch((e) => console.error(e));
        }, [utilisateurApi, autoload]);

        return (
            <UsersContext.Provider value={state}>
                <UsersDispatchContext.Provider value={dispatch}>
                    {children}
                </UsersDispatchContext.Provider>
            </UsersContext.Provider>
        );
    };

export function useUsersCtx() {
    const state = useContext(UsersContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getUsers = (): Array<User> => state?.data || [];

    const getUserById = (id: number): User | undefined =>
        (state?.data || []).find((user) => user.id === id);

    const getUsersFiltered = (filters: Partial<Record<keyof User, unknown>>): Array<User> =>
        (state?.data || []).filter((item) =>
            Object.entries(filters).every(([key, value]) => item[key as keyof User] === value),
        );

    const getUserFiltered = (filters: Partial<Record<keyof User, unknown>>): User | undefined =>
        getUsersFiltered(filters)[0] || undefined;

    return {
        isDataLoaded,
        getUsers,
        getUserById,
        getUsersFiltered,
        getUserFiltered,
    };
}

export function useUsersCtxDispatch() {
    return useContext(UsersDispatchContext);
}
